import { compare } from "compare-versions";
import { BifrostWeb } from "@seekrtech/bifrost-web";
import { fetchExperimentProducts, getSkuForPlatform, inferProductType, mapPlatformToServiceApi } from "@forest/paywall-core";
import pRetry from "p-retry";
//#region src/deferred.ts
function createDeferred() {
	let resolve;
	return {
		promise: new Promise((r) => {
			resolve = r;
		}),
		done: resolve
	};
}
//#endregion
//#region src/page-visible-internal.ts
let current = createDeferred();
const pageVisibleOnce = {
	get promise() {
		return current.promise;
	},
	done: (v) => current.done(v),
	/** @internal — used by __resetForTests in native.ts */
	__resetForTests() {
		current = createDeferred();
	}
};
//#endregion
//#region src/native.ts
let proxies = null;
let appVersion = "0.0.0";
/** @internal — set by initializeNativeBridge. Throws if called twice. */
function __setProxies(next) {
	if (proxies) throw new Error("native services already initialized");
	proxies = next;
}
/** @internal — set by initializeNativeBridge. */
function __setAppVersion(next) {
	appVersion = next;
}
function requireProxy(key) {
	if (!proxies) throw new Error("native services not initialized — call initializeNativeBridge() first");
	return proxies[key];
}
const native = {
	auth: {
		auth: () => requireProxy("auth").auth(),
		bindLegacyUser: () => requireProxy("auth").bindLegacyUser()
	},
	user: {
		getUserInfo: () => requireProxy("user").getUserInfo(),
		refreshToken: () => requireProxy("user").refreshToken(),
		refreshServiceLevel: () => requireProxy("user").refreshServiceLevel()
	},
	permission: {
		getHasNotificationPermission: () => requireProxy("permission").getHasNotificationPermission(),
		requestNotificationPermission: () => requireProxy("permission").requestNotificationPermission()
	},
	general: {
		close: () => requireProxy("general").close(),
		logEvent: (p) => {
			console.log("[native-services] logEvent", p.event_name, JSON.stringify(p.event_params ?? {}));
			return requireProxy("general").logEvent(p);
		},
		openURL: (url) => requireProxy("general").openURL(url),
		pageReady: () => requireProxy("general").pageReady(),
		pageAbort: () => requireProxy("general").pageAbort(),
		complete: () => requireProxy("general").complete(),
		getPageContext: () => requireProxy("general").getPageContext(),
		logFatalError: (p) => requireProxy("general").logFatalError(p),
		logNonFatalError: (p) => requireProxy("general").logNonFatalError(p)
	},
	data: { fullSync: () => requireProxy("data").fullSync() },
	iap: {
		getPrice: (p) => requireProxy("iap").getPrice(p),
		purchase: (p) => requireProxy("iap").purchase(p),
		closeTransaction: (p) => requireProxy("iap").closeTransaction(p),
		getUnclosedTransaction: (p) => requireProxy("iap").getUnclosedTransaction(p),
		getAllUnclosedTransactions: () => requireProxy("iap").getAllUnclosedTransactions(),
		getAllEntitlements: () => requireProxy("iap").getAllEntitlements(),
		getAvailableSubscriptionProducts: () => requireProxy("iap").getAvailableSubscriptionProducts(),
		getStoreRegion: () => requireProxy("iap").getStoreRegion(),
		getReceiptSystemAccountId: () => requireProxy("iap").getReceiptSystemAccountId()
	},
	additional: { getInitialThemeId: () => requireProxy("additional").getInitialThemeId() },
	l10n: { getLocalizedString: async (p) => {
		if (!compare(appVersion, "5.17.0", ">=")) return p.key;
		const result = await requireProxy("l10n").getLocalizedString(p);
		if (!result.success) return p.key;
		return result.data ?? p.key;
	} }
};
//#endregion
//#region src/page-visible-handlers.ts
function defaultSetSafeAreaInsets(payload) {
	Object.entries(payload).forEach(([key, value]) => {
		document.documentElement.style.setProperty(`--safe-area-${key}`, `${Math.round(value)}px`);
	});
}
//#endregion
//#region src/services/auth.ts
function createAuthService(bifrost) {
	return bifrost.createProxyBuilder("AuthService").withDefaultOptions({ timeout: 31536e6 }).build();
}
//#endregion
//#region src/services/user.ts
function createUserService(bifrost) {
	return bifrost.createProxyBuilder("UserService").withDefaultOptions({ timeout: 3e4 }).build();
}
//#endregion
//#region src/services/permission.ts
function createPermissionService(bifrost) {
	return bifrost.createProxyBuilder("PermissionService").withDefaultOptions({ timeout: 31536e6 }).build();
}
//#endregion
//#region src/services/general.ts
function createGeneralService(bifrost) {
	return bifrost.createNativeProxy("GeneralService");
}
//#endregion
//#region src/services/iap.ts
function createIAPService(bifrost) {
	return bifrost.createProxyBuilder("IAPService").withDefaultOptions({ timeout: 31536e6 }).build();
}
//#endregion
//#region src/services/additional.ts
function createAdditionalService(bifrost) {
	return bifrost.createNativeProxy("RelaxPaywallService");
}
//#endregion
//#region src/services/l10n.ts
function createL10nService(bifrost) {
	return bifrost.createNativeProxy("L10nService");
}
//#endregion
//#region src/services/data.ts
function createDataService(bifrost) {
	return bifrost.createNativeProxy("DataService");
}
//#endregion
//#region src/bifrost.ts
/** Must be called exactly once before any `native.*` call. Throws if called twice. */
function initializeNativeBridge(options = {}) {
	const bifrost = options._bifrost ?? new BifrostWeb(options.bifrost ?? {});
	__setAppVersion(options.appVersion ?? "0.0.0");
	__setProxies({
		auth: createAuthService(bifrost),
		user: createUserService(bifrost),
		permission: createPermissionService(bifrost),
		general: createGeneralService(bifrost),
		iap: createIAPService(bifrost),
		data: createDataService(bifrost),
		additional: createAdditionalService(bifrost),
		l10n: createL10nService(bifrost)
	});
	const setSafeAreaInsets = options.onSetSafeAreaInsets ?? defaultSetSafeAreaInsets;
	bifrost.registerService("GeneralService", {
		pageVisible: () => {
			options.onPageVisible?.();
			pageVisibleOnce.done();
		},
		setSafeAreaInsets
	});
	return {
		bifrost,
		ready: bifrost.initialize()
	};
}
//#endregion
//#region src/products/annual-double-trial.ts
const ANNUAL_DOUBLE_TRIAL = {
	production: {
		ios: {
			product_id: 67,
			sku: "forest_annual_early_bird_2"
		},
		googleplay_android: {
			product_id: 67,
			sku: "forest_plus-annual-early-bird-2"
		},
		china_android: null
	},
	staging: {
		ios: {
			product_id: 64,
			sku: "forest_annual_early_bird_2"
		},
		googleplay_android: {
			product_id: 64,
			sku: "forest_plus-annual-early-bird-2"
		},
		china_android: null
	},
	dev: {
		ios: {
			product_id: 98,
			sku: "forest_annual_early_bird_2"
		},
		googleplay_android: {
			product_id: 98,
			sku: "forest_plus-annual-early-bird-2"
		},
		china_android: null
	}
};
/**
* Builds the Arm A "rescue" product (annual plan with a doubled, 14-day trial)
* from a hardcoded `{ product_id, sku }` config, merging price / offers /
* trial-days from the native subscription product when available. Mirrors
* `buildMonthlyNoTrial`; the trial discount is a native-applied introductory
* offer, so no `offer_id` is selected here.
*/
function buildAnnualDoubleTrial(config, subscriptionProducts) {
	const nativeProduct = subscriptionProducts.find((p) => p.sku === config.sku);
	return {
		product_id: config.product_id,
		receipt_system_product_id: 0,
		sku: config.sku,
		product_type: "subscription",
		duration: "yearly",
		eligible_trial_days: nativeProduct?.eligible_trial_days ?? 0,
		offers: nativeProduct?.offers,
		price_localized: nativeProduct?.price_localized ?? "",
		price_value: nativeProduct?.price_value ?? 0,
		display_name: nativeProduct?.display_name ?? ""
	};
}
//#endregion
//#region src/products/monthly-7days-trial.ts
const MONTHLY_7DAYS_TRIAL = {
	production: {
		ios: {
			product_id: 114,
			sku: "forest_monthly_early_bird_4"
		},
		googleplay_android: {
			product_id: 114,
			sku: "forest_plus-monthly-early-bird-4"
		},
		china_android: null
	},
	staging: {
		ios: {
			product_id: 114,
			sku: "forest_monthly_early_bird_4"
		},
		googleplay_android: {
			product_id: 114,
			sku: "forest_plus-monthly-early-bird-4"
		},
		china_android: null
	},
	dev: {
		ios: {
			product_id: 97,
			sku: "forest_monthly_early_bird_4"
		},
		googleplay_android: {
			product_id: 97,
			sku: "forest_plus-monthly-early-bird-4"
		},
		china_android: null
	}
};
/**
* Builds the Arm B "rescue" product (monthly plan with a 7-day trial) from a
* hardcoded `{ product_id, sku }` config, merging price / offers / trial-days
* from the native subscription product when available. Mirrors
* `buildAnnualDoubleTrial`; the trial is a native-applied introductory offer, so
* no `offer_id` is selected here.
*/
function buildMonthly7daysTrial(config, subscriptionProducts) {
	const nativeProduct = subscriptionProducts.find((p) => p.sku === config.sku);
	return {
		product_id: config.product_id,
		receipt_system_product_id: 0,
		sku: config.sku,
		product_type: "subscription",
		duration: "monthly",
		eligible_trial_days: nativeProduct?.eligible_trial_days ?? 0,
		offers: nativeProduct?.offers,
		price_localized: nativeProduct?.price_localized ?? "",
		price_value: nativeProduct?.price_value ?? 0,
		display_name: nativeProduct?.display_name ?? ""
	};
}
//#endregion
//#region src/products/monthly-no-trial.ts
const MONTHLY_NO_TRIAL = {
	production: {
		ios: {
			product_id: 64,
			sku: "forest_monthly_early_bird_2"
		},
		googleplay_android: {
			product_id: 64,
			sku: "forest_plus-monthly-early-bird-2"
		},
		china_android: null
	},
	staging: {
		ios: {
			product_id: 68,
			sku: "forest_monthly_early_bird_2"
		},
		googleplay_android: {
			product_id: 68,
			sku: "forest_plus-monthly-early-bird-2"
		},
		china_android: null
	},
	dev: {
		ios: {
			product_id: 57,
			sku: "forest_monthly_early_bird_2"
		},
		googleplay_android: {
			product_id: 57,
			sku: "forest_plus-monthly-early-bird-2"
		},
		china_android: null
	}
};
function buildMonthlyNoTrial(config, subscriptionProducts) {
	const nativeProduct = subscriptionProducts.find((p) => p.sku === config.sku);
	return {
		product_id: config.product_id,
		receipt_system_product_id: 0,
		sku: config.sku,
		product_type: "subscription",
		duration: "monthly",
		eligible_trial_days: 0,
		offers: nativeProduct?.offers,
		price_localized: nativeProduct?.price_localized ?? "",
		price_value: nativeProduct?.price_value ?? 0,
		display_name: nativeProduct?.display_name ?? ""
	};
}
//#endregion
//#region src/products/canonical-mock.ts
function wait(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}
const annualOriginal = {
	product_id: 21,
	receipt_system_product_id: 0,
	sku: "forest_annual_1",
	product_type: "subscription",
	duration: "yearly",
	eligible_trial_days: 0,
	price_localized: "$200.00",
	price_value: 200,
	display_name: "Annual Plan"
};
const annualEarlyBird = {
	product_id: 22,
	receipt_system_product_id: 0,
	sku: "forest_annual_early_bird",
	product_type: "subscription",
	duration: "yearly",
	eligible_trial_days: 7,
	price_localized: "$100.00",
	price_value: 100,
	display_name: "Annual Plan (Early Bird)"
};
const monthlyOriginal = {
	product_id: 23,
	receipt_system_product_id: 0,
	sku: "forest_monthly_1",
	product_type: "subscription",
	duration: "monthly",
	eligible_trial_days: 0,
	price_localized: "$20.00",
	price_value: 20,
	display_name: "Monthly Plan"
};
const monthlyEarlyBird = {
	product_id: 24,
	receipt_system_product_id: 0,
	sku: "forest_monthly_early_bird",
	product_type: "subscription",
	duration: "monthly",
	eligible_trial_days: 7,
	price_localized: "$10.00",
	price_value: 10,
	display_name: "Monthly Plan (Early Bird)"
};
async function canonicalMock(env, platform) {
	await wait(1e3);
	const noTrialConfig = MONTHLY_NO_TRIAL[env]?.[platform] ?? null;
	const monthlyNoTrial = noTrialConfig ? {
		product_id: noTrialConfig.product_id,
		receipt_system_product_id: 0,
		sku: noTrialConfig.sku,
		product_type: "subscription",
		duration: "monthly",
		eligible_trial_days: 0,
		price_localized: "$10.00",
		price_value: 10,
		display_name: "Monthly Plan (No Trial)"
	} : null;
	const doubleTrialConfig = ANNUAL_DOUBLE_TRIAL[env]?.[platform] ?? null;
	const annualDoubleTrial = doubleTrialConfig ? {
		product_id: doubleTrialConfig.product_id,
		receipt_system_product_id: 0,
		sku: doubleTrialConfig.sku,
		product_type: "subscription",
		duration: "yearly",
		eligible_trial_days: 14,
		price_localized: "$100.00",
		price_value: 100,
		display_name: "Annual Plan (14-day trial)"
	} : null;
	const sevenDaysTrialConfig = MONTHLY_7DAYS_TRIAL[env]?.[platform] ?? null;
	const monthly7daysTrial = sevenDaysTrialConfig ? {
		product_id: sevenDaysTrialConfig.product_id,
		receipt_system_product_id: 0,
		sku: sevenDaysTrialConfig.sku,
		product_type: "subscription",
		duration: "monthly",
		eligible_trial_days: 7,
		price_localized: "$10.00",
		price_value: 10,
		display_name: "Monthly Plan (7-day trial)"
	} : null;
	return {
		annualOriginal,
		annualEarlyBird,
		monthlyOriginal,
		monthlyEarlyBird,
		monthlyNoTrial,
		annualDoubleTrial,
		monthly7daysTrial
	};
}
//#endregion
//#region src/products/cache.ts
const TTL_MS = 3600 * 1e3;
function readCached(key) {
	try {
		const raw = localStorage.getItem(key);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (Date.now() - parsed.timestamp > TTL_MS) {
			localStorage.removeItem(key);
			return null;
		}
		return parsed.data;
	} catch (error) {
		console.error("Error reading cache from localStorage:", error);
		localStorage.removeItem(key);
		return null;
	}
}
function writeCached(key, data) {
	const envelope = {
		data,
		timestamp: Date.now()
	};
	localStorage.setItem(key, JSON.stringify(envelope));
}
function readSubscriptionProductsCache(key) {
	return readCached(key);
}
function writeSubscriptionProductsCache(key, value) {
	writeCached(key, value);
}
function readExpSkusCache(key) {
	return readCached(key);
}
function writeExpSkusCache(key, value) {
	writeCached(key, value);
}
//#endregion
//#region src/products/merge.ts
/**
* Fallback used on app versions below 5.2.0 that do not support
* `getAvailableSubscriptionProducts`. Synthesizes `SubscriptionProduct[]` by
* calling `getPrice` once per SKU so downstream merging is uniform.
*/
async function getSubscriptionProductsBySkus(expSkus, platform, iap) {
	return Promise.all(expSkus.map(async (apiProduct) => {
		const sku = getSkuForPlatform(apiProduct, platform);
		const priceResult = await iap.getPrice({
			product_id: 0,
			product_type: "subscription",
			sku
		});
		if (!priceResult.success) throw new Error(priceResult.error);
		const { isAnnual } = inferProductType(apiProduct.title);
		return {
			sku,
			duration: isAnnual ? "yearly" : "monthly",
			eligible_trial_days: 0,
			price_localized: priceResult.data.price_localized,
			price_value: priceResult.data.price_value,
			display_name: ""
		};
	}));
}
function mergeProducts(expSkus, subscriptionProducts, platform) {
	const nativeBySku = new Map(subscriptionProducts.map((p) => [p.sku, p]));
	const collected = {};
	for (const apiProduct of expSkus) {
		const sku = getSkuForPlatform(apiProduct, platform);
		const native = nativeBySku.get(sku);
		if (!native) throw new Error(`Product not found for SKU: ${sku}`);
		const { isAnnual, isEarlyBird } = inferProductType(apiProduct.title);
		const built = {
			product_id: apiProduct.id,
			receipt_system_product_id: 0,
			sku,
			product_type: "subscription",
			duration: native.duration,
			eligible_trial_days: native.eligible_trial_days,
			offers: native.offers,
			price_localized: native.price_localized,
			price_value: native.price_value,
			display_name: native.display_name
		};
		if (isAnnual && isEarlyBird) collected.annualEarlyBird = built;
		else if (isAnnual) collected.annualOriginal = built;
		else if (isEarlyBird) collected.monthlyEarlyBird = built;
		else collected.monthlyOriginal = built;
	}
	if (!collected.annualOriginal || !collected.annualEarlyBird || !collected.monthlyOriginal || !collected.monthlyEarlyBird) throw new Error("Missing one of annual/monthly original/early-bird products");
	return collected;
}
//#endregion
//#region src/products/get-products.ts
async function getProducts(options) {
	const { cacheKeyPrefix, api, appVersion, platform, deferredUserInfo, env, shouldMock } = options;
	if (shouldMock) return canonicalMock(env, platform);
	const supportsGetStoreRegion = compare(appVersion, "5.7.0", ">=");
	const supportsGetAvailableSubs = compare(appVersion, "5.2.0", ">=");
	const subsCacheKey = `${cacheKeyPrefix}.subs.${env}.${platform}`;
	const cachedSubs = supportsGetAvailableSubs ? readSubscriptionProductsCache(subsCacheKey) : null;
	const promiseSubscriptionProducts = cachedSubs ? Promise.resolve(cachedSubs) : supportsGetAvailableSubs ? pRetry(async () => {
		const r = await native.iap.getAvailableSubscriptionProducts();
		if (!r.success) throw new Error(r.error);
		return r.data.products;
	}) : null;
	const promiseStoreRegion = supportsGetStoreRegion ? native.iap.getStoreRegion().then((r) => r.success ? r.data.region : null).catch(() => null) : Promise.resolve(null);
	const userInfo = await deferredUserInfo;
	const expCacheKey = `${cacheKeyPrefix}.exp.${userInfo.user_id}`;
	const cachedExp = readExpSkusCache(expCacheKey);
	const promiseExpSkus = cachedExp ? Promise.resolve(cachedExp) : promiseStoreRegion.then((storeRegion) => fetchExperimentProducts(api.service, {
		userId: userInfo.user_id,
		platform: mapPlatformToServiceApi(platform),
		storeRegion
	}));
	const finalSubscriptionProducts = promiseSubscriptionProducts ?? pRetry(async () => {
		return getSubscriptionProductsBySkus(await promiseExpSkus, platform, native.iap);
	});
	const [expSkus, subscriptionProducts] = await Promise.all([promiseExpSkus, finalSubscriptionProducts]);
	if (!cachedSubs && supportsGetAvailableSubs) writeSubscriptionProductsCache(subsCacheKey, subscriptionProducts);
	if (!cachedExp) writeExpSkusCache(expCacheKey, expSkus);
	const four = mergeProducts(expSkus, subscriptionProducts, platform);
	const noTrialConfig = MONTHLY_NO_TRIAL[env]?.[platform] ?? null;
	const monthlyNoTrial = noTrialConfig ? buildMonthlyNoTrial(noTrialConfig, subscriptionProducts) : null;
	const doubleTrialConfig = ANNUAL_DOUBLE_TRIAL[env]?.[platform] ?? null;
	const annualDoubleTrial = doubleTrialConfig ? buildAnnualDoubleTrial(doubleTrialConfig, subscriptionProducts) : null;
	const sevenDaysTrialConfig = MONTHLY_7DAYS_TRIAL[env]?.[platform] ?? null;
	const monthly7daysTrial = sevenDaysTrialConfig ? buildMonthly7daysTrial(sevenDaysTrialConfig, subscriptionProducts) : null;
	return {
		...four,
		monthlyNoTrial,
		annualDoubleTrial,
		monthly7daysTrial
	};
}
//#endregion
//#region src/products/receipt-system.ts
async function getReceiptSystemAccountIdSafe(appVersion) {
	if (!compare(appVersion, "5.10.0", ">=")) return null;
	try {
		return await pRetry(async () => {
			const result = await native.iap.getReceiptSystemAccountId();
			if (!result.success) throw new Error(result.error);
			return result.data.receipt_system_account_id;
		}, { retries: 2 });
	} catch (error) {
		native.general.logNonFatalError({
			name: "failed_to_get_receipt_system_account_id",
			code: 1,
			params: {
				message: String(error),
				app_version: appVersion
			}
		}).catch(() => {});
		return null;
	}
}
//#endregion
//#region src/products/store-region.ts
async function getStoreRegionSafe(appVersion) {
	if (!compare(appVersion, "5.7.0", ">=")) return null;
	const result = await native.iap.getStoreRegion();
	if (!result.success) return null;
	return result.data.region;
}
//#endregion
export { createDeferred, getProducts, getReceiptSystemAccountIdSafe, getStoreRegionSafe, initializeNativeBridge, native, pageVisibleOnce };

//# sourceMappingURL=index.js.map